import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import axios from "../api";
import { getAuthRedirectUrl } from "../utils/authRedirect";

function qrUrlFromValue(value) {
    const payload = encodeURIComponent(String(value));
    return `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${payload}`;
}

const pad2 = (n) => String(n).padStart(2, "0");
const localYMD = (d = new Date()) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

function passStatusLabel(p) {
    const st = String(p?.status || "").toLowerCase();
    const today = localYMD();
    const start = String(p?.start_date || "");
    const end = String(p?.end_date || "");
    if (st === "unactivated" || !start || !end) return "Ready to Activate";
    if (today < start) return "Not Redeemable";
    if (today > end) return "Expired";
    return "Active";
}

function isAdultPass(pass) {
    const holderType = String(pass?.holder_type || "adult").toLowerCase();
    const audience = String(pass?.pass_types?.audience || "").toLowerCase();

    return holderType !== "child" && audience !== "child";
}

function isPurchaserViewOnly(pass) {
    return pass?.managed_outbound === true || String(pass?.access_role || "").toLowerCase() === "purchaser_view";
}

function pickCurrentPass(passes) {
    if (!Array.isArray(passes) || passes.length === 0) return null;

    const adultPasses = passes.filter((pass) => isAdultPass(pass) && !isPurchaserViewOnly(pass));
    const candidates = adultPasses.length ? adultPasses : passes.filter(isAdultPass);

    if (candidates.length === 0) return null;

    const today = localYMD();

    const byCreatedOldest = (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0);
    const byCreatedNewest = (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);

    const active = candidates
        .filter((p) => p?.start_date && p?.end_date && p.start_date <= today && today <= p.end_date)
        .sort(byCreatedOldest)[0];
    if (active) return active;

    const unactivated = candidates
        .filter((p) => {
            const st = String(p?.status || "").toLowerCase();
            return st === "unactivated" || !p?.start_date || !p?.end_date;
        })
        .sort(byCreatedOldest)[0];
    if (unactivated) return unactivated;

    const upcoming = candidates
        .filter((p) => p?.start_date && today < p.start_date)
        .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)) || byCreatedOldest(a, b))[0];
    if (upcoming) return upcoming;

    return (
        candidates
            .filter((p) => p?.end_date && today > p.end_date)
            .sort((a, b) => String(b.end_date).localeCompare(String(a.end_date)) || byCreatedNewest(a, b))[0] || null
    );
}

export default function AccountWeb() {
    const [user, setUser] = useState(null);
    const [passes, setPasses] = useState([]);
    const [status, setStatus] = useState("");
    const [busy, setBusy] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        const init = async () => {
            const { data } = await supabase.auth.getSession();
            if (data?.session?.user) {
                setUser(data.session.user);
                fetchPasses();
            }
        };
        init();

        const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
            if (session?.user) {
                setUser(session.user);
                fetchPasses();
            } else {
                setUser(null);
                setPasses([]);
            }
        });

        return () => {
            sub?.subscription?.unsubscribe?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchPasses = async () => {
        try {
            const { data } = await supabase.auth.getSession();
            const token = data?.session?.access_token;

            if (!token) {
                setPasses([]);
                return;
            }

            const res = await axios.get("/passes", {
                headers: { Authorization: `Bearer ${token}` },
            });

            setPasses(res.data || []);
        } catch (err) {
            console.error("Error fetching passes", err?.response?.data || err);
            setStatus("Could not load your pass.");
        }
    };

    const currentPass = useMemo(() => pickCurrentPass(passes), [passes]);
    const badgeText = currentPass ? passStatusLabel(currentPass) : "No Pass";
    const badgeClass =
        badgeText === "Active"
            ? "active"
            : badgeText === "Expired" || badgeText === "Trip Window Passed"
                ? "expired"
                : badgeText === "Not Redeemable" || badgeText === "Not Redeemable Yet" || badgeText === "Ready to Activate"
                    ? "scheduled"
                    : "idle";

    const handleLogout = async () => {
        setStatus("");
        setBusy(true);
        try {
            const { error } = await supabase.auth.signOut({ scope: "local" });
            if (error) console.warn("Logout (local) error:", error.message);
            window.location.href = "/";
        } finally {
            setBusy(false);
        }
    };

    const copyPassId = async () => {
        if (!currentPass?.id) return;
        try {
            await navigator.clipboard.writeText(currentPass.id);
            setStatus("Copied pass ID.");
        } catch {
            setStatus("Could not copy pass ID.");
        }
    };

    const sharePassId = async () => {
        if (!currentPass?.id) return;
        const text = `Beach Bum Pass ID: ${currentPass.id}`;
        try {
            if (navigator.share) {
                await navigator.share({ title: "Beach Bum Pass", text });
                setStatus("Shared.");
                return;
            }
        } catch {
            // fall through
        }
        await copyPassId();
    };

    const exportPassPdf = async () => {
        if (!currentPass?.id) return;

        setStatus("");
        setBusy(true);
        try {
            const { data } = await supabase.auth.getSession();
            const token = data?.session?.access_token;
            if (!token) {
                setStatus("Please log in again.");
                return;
            }

            const res = await axios.get(`/passes/${currentPass.id}/pdf`, {
                responseType: "blob",
                headers: { Authorization: `Bearer ${token}` },
            });

            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            setStatus("Pass PDF opened.");
        } catch (e) {
            console.error("PDF export error:", e?.response?.data || e);
            setStatus("Could not export pass PDF.");
        } finally {
            setBusy(false);
        }
    };

    const resetWalkthrough = async () => {
        const { data } = await supabase.auth.getSession();
        const userId = data?.session?.user?.id;
        if (userId) localStorage.removeItem(`bbp_onboarded_v1_${userId}`);
        setStatus("Walkthrough reset. Go back to Home to see it again.");
    };


    const WebItem = ({ title, sub, onClick, right = "Open", danger = false }) => (
        <button
            type="button"
            className={`account-web-item ${danger ? "account-web-item--danger" : ""}`}
            onClick={onClick}
        >
            <div>
                <div className="account-web-item__title">{title}</div>
                {sub ? <div className="account-web-item__sub">{sub}</div> : null}
            </div>
            <span className="account-web-item__cta">{right}</span>
        </button>
    );

    if (!user) {
        return (
            <div className="account-web account-web--refresh">
                <section className="account-web-hero account-web-hero--refresh">
                    <div className="account-web-hero__copy">
                        <div className="account-web-hero__eyebrow">Beach Bum Account</div>
                        <h1>Log in to manage your coast pass.</h1>
                        <p>Your pass, your crew, and your coastal game plan all live here.</p>
                    </div>
                </section>

                <section className="account-web-panel account-web-panel--refresh">
                    <div className="account-web-empty">
                        <h2>Log in required</h2>
                        <p>Create an account or log in to manage your pass and crew details.</p>
                        <div className="account-web-cta-row">
                            <button className="passes-web-btn primary" type="button" onClick={() => nav("/login")}>Log In</button>
                            <button className="passes-web-btn secondary" type="button" onClick={() => nav("/register")}>Create Account</button>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="account-web account-web--refresh">
            <section className="account-web-hero account-web-hero--refresh">
                <div className="account-web-hero__copy">
                    <div className="account-web-hero__eyebrow">Beach Bum Account</div>
                    <h1>Captain’s Quarters</h1>
                    <p>Manage your pass, claim crew access, update your profile, and keep the important stuff close without clutter.</p>
                </div>
                <div className="account-web-hero__meta">
                    <div className="account-web-pill">{user.email}</div>
                </div>
            </section>

            {status ? <div className="account-web-status">{status}</div> : null}

            <div className="account-web-layout">
                <aside className="account-web-rail">
                    <div className="account-web-rail__card">
                        <div className="account-web-rail__title">Jump to</div>
                        <a href="#account-overview">Overview</a>
                        <a href="#account-passes">Pass Management</a>
                        <a href="#account-profile">Profile & Help</a>
                    </div>
                </aside>

                <div className="account-web-main">
                    <section className="account-web-panel account-web-panel--feature account-web-panel--refresh" id="account-overview">
                        <div className="account-web-panel__head">
                            <div>
                                <div className="account-web-section-label">Overview</div>
                                <h2>Your Beach Pass</h2>

                            </div>
                        </div>

                        <div className="account-web-pass-hero account-web-pass-hero--refresh">
                            <div className="account-web-pass-hero__body">
                                <div className="account-web-pass-hero__name">
                                    {currentPass ? currentPass.pass_types?.name || "Beach Bum Pass" : "No pass yet"}
                                </div>

                                <div className="account-web-pass-hero__copy">
                                    {currentPass
                                        ? "Scan this at your first stop to activate your pass. Once you're rolling, keep it ready for every stop down the coast."
                                        : "No pass in the beach bag yet. You can still manage invites and profile details from here."}
                                </div>

                                <div className="account-web-pass-stats account-web-pass-stats--refresh">
                                    <div className="account-web-pass-stat">
                                        <span>Status</span>
                                        <strong>{badgeText}</strong>
                                    </div>
                                    <div className="account-web-pass-stat">
                                        <span>Duration</span>
                                        <strong>{currentPass?.pass_types?.duration_days ?? "—"} days</strong>
                                    </div>
                                    <div className="account-web-pass-stat">
                                        <span>Pass ID</span>
                                        <strong className="mono">{currentPass ? `${String(currentPass.id).slice(0, 8)}…` : "—"}</strong>
                                    </div>
                                </div>

                                <div className="account-web-cta-row">
                                    <button className="passes-web-btn secondary" type="button" onClick={copyPassId} disabled={!currentPass || busy}>Copy Pass ID</button>
                                    <button className="passes-web-btn primary" type="button" onClick={() => nav("/passes")}>Open Passes</button>
                                </div>
                            </div>

                            <div className="account-web-pass-hero__qr">
                                {currentPass ? (
                                    <>
                                        <div className="account-web-pass-qr-shell">
                                            <img className="pass-qr" alt="Pass QR Code" src={qrUrlFromValue(currentPass.id)} />
                                        </div>
                                        <div className="pass-qr-caption">Scan at Activity</div>
                                    </>
                                ) : (
                                    <div className="account-web-empty-qr">
                                        <div className="account-web-empty-qr__icon">🏖️</div>
                                        <div className="account-web-empty-qr__copy">Once you have a pass, your live QR will show here.</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>

                    <section className="account-web-stack">
                        <section className="account-web-panel account-web-panel--wide account-web-panel--refresh" id="account-passes">
                            <div className="account-web-section-label">Passes</div>
                            <h3>Pass Management</h3>
                            <p className="account-web-panel__copy">
                                Claim emailed passes, manage your crew, and open the full passes page when you need the builder.
                            </p>

                            <div className="account-web-list account-web-list--two">
                                <WebItem title="Redeem Pass" sub="Claim a pass that was emailed to you" onClick={() => nav("/passes#claim")} />
                                <WebItem title="Crew Passes" sub="View and assign Grommet and Little Bum passes attached to your account" onClick={() => nav("/passes#child-passes")} />
                                <WebItem title="Export Pass PDF" sub="Save or share your pass as a PDF" onClick={exportPassPdf} />
                                <WebItem title="Copy / Share Pass ID" sub="Quickly share your pass code" onClick={sharePassId} />
                                <WebItem title="Open Passes Page" sub="Go to the full passes experience" onClick={() => nav("/passes")} />
                            </div>
                        </section>

                        <section className="account-web-panel account-web-panel--wide account-web-panel--refresh" id="account-profile">
                            <div className="account-web-section-label">Profile</div>
                            <h3>Profile & Help</h3>
                            <p className="account-web-panel__copy">
                                Profile details, help docs, security controls, and account actions are stowed here together.
                            </p>

                            <div className="account-web-subsections">
                                <div className="account-web-subsection">
                                    <div className="account-web-subsection__title">Profile & Help</div>
                                    <div className="account-web-list">
                                        <WebItem title="Profile Management" sub="" onClick={() => nav("/account/profile")} />
                                        <WebItem title="How it Works" sub="Pass activation, reservations, and scan rules" onClick={() => nav("/how-it-works")} />
                                    </div>
                                </div>

                                <div className="account-web-subsection">
                                    <div className="account-web-subsection__title">Security & Login</div>
                                    <div className="account-web-list">
                                        <WebItem
                                            title="Change Password"
                                            sub="Send yourself a reset link"
                                            onClick={async () => {
                                                setStatus("");
                                                setBusy(true);
                                                try {
                                                    const cleanEmail = user.email;
                                                    const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
                                                        redirectTo: getAuthRedirectUrl("/auth/update-password"),
                                                    });
                                                    if (error) return setStatus(error.message);
                                                    setStatus("Password reset email sent.");
                                                } finally {
                                                    setBusy(false);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="account-web-subsection">
                                    <div className="account-web-subsection__title">Account</div>
                                    <div className="account-web-list">
                                        <WebItem title="Log Out" sub="Sign out of this device" onClick={handleLogout} />
                                    </div>
                                </div>
                            </div>
                        </section>


                    </section>
                </div>
            </div>
        </div>
    );
}